import { Root } from "mdast";
import { Plugin } from "unified";
import { visit } from "unist-util-visit";

/**
 * Remark plugin that extracts the associatedSentence for all paper_cite nodes.
 * This is used downstream for analytics.
 *
 * The plugin:
 * 1. Visits all paper_cite nodes in the AST
 * 2. Reconstructs the surrounding text context
 * 3. Extracts the sentence or phrase preceding the citation
 * 4. Cleans table markup if needed
 * 5. Adds associatedSentence property to the node
 */
// Helper function to check if a node is inside a table cell
const isInTableCell = (parent: any): boolean => {
  // For markdown tables, check if parent text contains table markup
  if (parent && parent.children) {
    const reconstructedText = reconstructTextContent(parent.children);
    return reconstructedText.includes("|");
  }
  return false;
};

// Helper function to clean table markup from context text
const cleanTableMarkup = (text: string): string => {
  // Split by pipe and get the last meaningful part (the cell with the citation)
  const parts = text.split("|").map((part) => part.trim());

  // Find the part that doesn't look like a separator or empty
  const meaningfulParts = parts.filter(
    (part) =>
      part.length > 0 &&
      !part.match(/^[\s\-]+$/) &&
      !part.match(/^Column\s+\d+$/i)
  );

  // Get the last meaningful part (should be the cell content)
  const lastPart = meaningfulParts[meaningfulParts.length - 1] || "";

  // Remove any remaining citation placeholders and return
  return lastPart.replace(/\{\{CITE\}\}/g, "").trim();
};

// Helper function to reconstruct text content from AST nodes
const reconstructTextContent = (nodes: any[]): string => {
  return nodes
    .map((node) => {
      if (node.type === "text") {
        return node.value;
      } else if (node.type === "paper_cite") {
        // Represent paper_cite as a placeholder for reconstruction
        return "{{CITE}}";
      } else if (node.children) {
        return reconstructTextContent(node.children);
      }
      return "";
    })
    .join("");
};

// Main function to extract associated sentence for a paper_cite node
const extractAssociatedSentence = (
  node: any,
  index: number | undefined,
  parent: any
): string => {
  if (!parent || !parent.children || index === undefined) {
    return "";
  }

  // Reconstruct the text content of the parent
  const reconstructedText = reconstructTextContent(parent.children);

  // Find all citation placeholders
  const citeMatches = Array.from(
    reconstructedText.matchAll(/\{\{CITE\}\}/g)
  ) as RegExpMatchArray[];

  if (citeMatches.length === 0) {
    return "";
  }

  // Find which citation this node corresponds to by index
  let nodeIndex = 0;
  for (let i = 0; i < parent.children.length; i++) {
    if (parent.children[i] === node) {
      break;
    }
    if (parent.children[i].type === "paper_cite") {
      nodeIndex++;
    }
  }

  if (nodeIndex >= citeMatches.length) {
    return "";
  }

  const currentMatch = citeMatches[nodeIndex];
  let contextStartIndex = currentMatch.index!;

  // For grouped citations, find the start of the group
  // Go backwards to find first citation in group
  let firstCitationIndex = nodeIndex;
  while (firstCitationIndex > 0) {
    const prevMatch = citeMatches[firstCitationIndex - 1];
    const gap =
      citeMatches[firstCitationIndex].index! -
      (prevMatch.index! + prevMatch[0].length);
    if (gap <= 1) {
      // Adjacent or single space
      firstCitationIndex--;
    } else {
      break;
    }
  }

  contextStartIndex = citeMatches[firstCitationIndex].index!;

  // Extract the text before the citation(s)
  const precedingText = reconstructedText
    .substring(0, contextStartIndex)
    .replace(/\{\{CITE\}\}/g, "") // Remove any citation placeholders
    .trim();

  // Only use as context if there's meaningful text
  if (precedingText.length === 0) {
    return "";
  }

  // Check if this is a table citation and clean markup
  const isTableCitation = isInTableCell(parent);
  if (isTableCitation) {
    // Set isTableCitation flag on the node
    if (!node.data) node.data = {};
    if (!node.data.hProperties) node.data.hProperties = {};
    node.data.hProperties.isTableCitation = true;

    return cleanTableMarkup(precedingText);
  }

  return precedingText;
};

export const remarkCitationContextExtraction: Plugin<[], Root> = () => {
  return (tree) => {
    // First pass: collect all paper_cite nodes with their contexts
    const citationContexts = new Map<any, string>();

    visit(tree, (node: any, index, parent) => {
      if (node.type === "paper_cite") {
        // Only extract context if no existing associatedSentence
        if (!node.data?.hProperties?.associatedSentence) {
          const associatedSentence = extractAssociatedSentence(
            node,
            index,
            parent
          );
          citationContexts.set(node, associatedSentence);
        }
      }
    });

    // Second pass: apply the contexts to the nodes
    visit(tree, (node: any) => {
      if (node.type === "paper_cite" && citationContexts.has(node)) {
        if (!node.data) node.data = {};
        if (!node.data.hProperties) node.data.hProperties = {};

        // Set the extracted context (we already checked for existing values in first pass)
        node.data.hProperties.associatedSentence = citationContexts.get(node);
      }
    });
  };
};
